import { useEffect, useMemo, useState } from "react";

export interface ViewportOptions {
  mobileBreakpoint?: number;
  tabletBreakpoint?: number;
  treatPortraitAsMobile?: boolean;
}

export interface ViewportState {
  width: number;
  height: number;
  isPortrait: boolean;
  isMobile: boolean;
  isTablet: boolean;
  isAndroidApp: boolean;
  isAndroidTablet: boolean;
  usedParentDimensions: boolean;
  usedFallbackMobile: boolean;
}

const MOBILE_UA_REGEX =
  /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;

function readViewport(options: ViewportOptions): ViewportState {
  const {
    mobileBreakpoint = 640,
    tabletBreakpoint = 1024,
    treatPortraitAsMobile = false,
  } = options;
  const userAgent =
    typeof navigator !== "undefined"
      ? (
          navigator.userAgent ||
          navigator.vendor ||
          ((window as any).opera as string | undefined) ||
          ""
        ).toLowerCase()
      : "";

  if (typeof window === "undefined") {
    return {
      width: mobileBreakpoint,
      height: tabletBreakpoint,
      isPortrait: false,
      isMobile: false,
      isTablet: false,
      isAndroidApp: false,
      isAndroidTablet: false,
      usedParentDimensions: false,
      usedFallbackMobile: false,
    };
  }

  let usedParentDimensions = false;
  let fallbackMobile = false;
  let fallbackPortrait: boolean | undefined;

  const inIframe = window.self !== window.top;
  if (inIframe) {
    try {
      if (window.parent && window.parent !== window.self) {
        const parentWidth = window.parent.innerWidth;
        const parentHeight = window.parent.innerHeight;
        if (parentWidth && parentHeight) {
          // Use parent dimensions if available (common for iframes)
          const width = Math.max(1, Math.round(parentWidth));
          const height = Math.max(1, Math.round(parentHeight));
          return {
            width,
            height,
            isPortrait: height >= width,
            isMobile: width < mobileBreakpoint || (treatPortraitAsMobile && height >= width),
            isTablet: width < tabletBreakpoint || width < mobileBreakpoint || (treatPortraitAsMobile && height >= width),
            usedParentDimensions: true,
            usedFallbackMobile: false,
          };
        }
      }
    } catch {
      // Fall through to visualViewport/client dimensions
      const hasTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
      fallbackMobile = hasTouch || MOBILE_UA_REGEX.test(userAgent);
      if (window.screen && window.screen.width && window.screen.height) {
        fallbackPortrait = window.screen.height > window.screen.width;
      }
    }
  }

  const docW = document.documentElement?.clientWidth || window.innerWidth;
  const docH = document.documentElement?.clientHeight || window.innerHeight;
  const vv = window.visualViewport;
  const vw = vv ? Math.round(vv.width) : undefined;
  const vh = vv ? Math.round(vv.height) : undefined;

  let width = docW;
  let height = docH;

  if (vv && vw && vh && vw > 0 && vh > 0) {
    // Use the smaller of visualViewport and doc to avoid overestimating width during chrome collapse
    width = Math.min(docW, vw);
    height = Math.min(docH, vh);
  }

  width = Math.max(1, Math.round(width));
  height = Math.max(1, Math.round(height));

  const isPortrait =
    fallbackPortrait !== undefined ? fallbackPortrait : height >= width;

  // Force Android (Capacitor) clients into the mobile/tablet buckets so tablets use native layouts
  const isAndroidApp =
    typeof window !== "undefined" &&
    (window as any).Capacitor &&
    (window as any).Capacitor.getPlatform &&
    (window as any).Capacitor.getPlatform() === "android";
  const isIOSApp =
    typeof window !== "undefined" &&
    (window as any).Capacitor &&
    (window as any).Capacitor.getPlatform &&
    (window as any).Capacitor.getPlatform() === "ios";
  const isIOSDevice = /ipad|iphone|ipod/.test(userAgent);
  // Use the larger of viewport and screen dimensions to avoid misclassifying tablets
  const screenW = typeof window !== "undefined" && window.screen ? window.screen.width : 0;
  const screenH = typeof window !== "undefined" && window.screen ? window.screen.height : 0;
  // If we're in the Android app, prefer the full screen dimensions once they are known.
  if (isAndroidApp && screenW && screenH) {
    width = Math.max(width, screenW);
    height = Math.max(height, screenH);
  }

  // Use screen orientation as a hint on Android app to avoid early portrait misreads during startup
  if (isAndroidApp && screenW && screenH) {
    fallbackPortrait = screenH >= screenW;
  }

  const maxDim = Math.max(width, height, screenW, screenH);
  const minDim = Math.min(width, height, screenW || width, screenH || height);
  // Heuristic: Android tablet if both dimensions exceed typical phone bounds
  const isAndroidTablet = isAndroidApp && maxDim >= 960 && minDim >= 600;

  const isMobile =
    width < mobileBreakpoint ||
    fallbackMobile ||
    isAndroidApp ||
    isIOSApp ||
    isIOSDevice ||
    (treatPortraitAsMobile && isPortrait);
  const isTablet =
    width < tabletBreakpoint ||
    fallbackMobile ||
    isMobile ||
    isAndroidApp ||
    isIOSApp ||
    isIOSDevice;

  return {
    width,
    height,
    isPortrait,
    isMobile,
    isTablet,
    isAndroidApp,
    isAndroidTablet,
    usedParentDimensions,
    usedFallbackMobile: fallbackMobile || isAndroidApp || isIOSApp || isIOSDevice,
  };
}

export function useViewport(
  options: ViewportOptions = {}
): ViewportState {
  const [state, setState] = useState<ViewportState>(() => readViewport(options));

  useEffect(() => {
    let raf = 0;
    let settleTimer: ReturnType<typeof setTimeout> | null = null;
    let settleTimer2: ReturnType<typeof setTimeout> | null = null;

    const update = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        setState(readViewport(options));
      });
    };

    window.addEventListener("resize", update, { passive: true });
    window.addEventListener("orientationchange", update, { passive: true });
    window.visualViewport?.addEventListener("resize", update, { passive: true });
    window.visualViewport?.addEventListener("scroll", update, { passive: true });

    update(); // initial sync after mount

    // On Android/Capacitor the first measurement can be early (before immersive UI),
    // so re-run once shortly after mount to pick up the settled viewport.
    const isAndroidApp =
      typeof window !== "undefined" &&
      (window as any).Capacitor &&
      (window as any).Capacitor.getPlatform &&
      (window as any).Capacitor.getPlatform() === "android";
    if (isAndroidApp) {
      settleTimer = setTimeout(update, 180);
      settleTimer2 = setTimeout(update, 420);
    }

    return () => {
      cancelAnimationFrame(raf);
      if (settleTimer) clearTimeout(settleTimer);
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
      window.visualViewport?.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("scroll", update);
      if (settleTimer) clearTimeout(settleTimer);
      if (settleTimer2) clearTimeout(settleTimer2);
    };
  }, [options]);

  // Memoize derived values to avoid churn
  const memoState = useMemo(() => {
    const isPortrait = state.isPortrait;
    const isMobile = state.isMobile;
    const isTablet = state.isTablet;
    return { ...state, isPortrait, isMobile, isTablet };
  }, [state]);

  return memoState;
}
